import React, { Component } from "react";
import PropTypes from "prop-types";
import "./AgentTransferSelector.scss";
import { AgentStatusView, AgentViewTypes } from "constants/ChatViewConstants";
import { UserStatus } from "constants/UserConstants";
import { uepClient } from "uepClient";
import { getUsername } from "utils/Common";
import Input from "components/Input/Input";
import Button from "Button/Button";
import CollapsibleTabs from "components/CollapsibleTabs/CollapsibleTabs";
import colors from "styles/colors.module.scss";
import MaterialTable from "material-table";
import { tableIcons } from "utils/MaterialTableHelper";
import { connect } from "react-redux";
import { changeAgentView } from "actions/agentViewActions";
import WithAlert from "hoc/WithAlert";
import { transferConversation } from "actions/conversationActions";
import { ConversationStatus } from "constants/ChatListingConstants";
import { getBusinessUnit, getGroup } from "utils/Common";

class AgentTransferSelector extends Component {
  static propTypes = {
    className: PropTypes.string,
    changeAgentView: PropTypes.func.isRequired,
    activeConversationId: PropTypes.string.isRequired,
    conversations: PropTypes.array,
    transferConversation: PropTypes.func.isRequired,
  };

  static defaultProps = {
    className: "",
    conversations: [],
  };

  constructor() {
    super();
    this.state = {
      searchText: "",
      selectedAgentStatus: AgentStatusView.READY,
      agentsList: [],
      isLoading: false,
      selectedAgent: null,
      selectedAgentGroup: null,
      searchedAgentsList: [],
      isTransferring: false,
    };
    this.businessUnit = getBusinessUnit();
    this.currentAgentId = getUsername();
    this.group = getGroup();
  }

  componentDidMount() {
    this.getLoggedInAgents();
  }

  getLoggedInAgents = () => {
    this.setState({
      isLoading: true,
    });
    uepClient()
      .get(`/stats/loggedin-agents?filterByGroup=false`)
      .then((res) => {
        const agentsList = res.data.agentStats
          .map((agent) => ({
            agentId: agent.agentId,
            name: `${agent.firstName} ${agent.lastName}`,
            activeSessionsCount: agent.activeSessionsCount,
            skill: agent.jobTitle || "Gen, Sales-PV",
            status: UserStatus[agent.status],
            group: agent.group,
          }))
          .filter((agent) => agent.agentId !== this.currentAgentId);
        this.setState({
          agentsList,
          isLoading: false,
        });
      });
  };

  onSearchTextChange = (e) => {
    const searchText = e.target.value;
    this.setState(
      {
        searchText: searchText,
      },
      () => {
        this.updateSearchedAgentsList(searchText);
      }
    );
  };

  updateSearchedAgentsList = (searchText) => {
    const { agentsList } = this.state;
    searchText = searchText.trim().toLowerCase();
    const searchedAgents = agentsList.filter(
      (agent) =>
        agent.name.trim().toLowerCase().includes(searchText) ||
        agent.group.trim().toLowerCase().includes(searchText)
    );
    this.setState({
      searchedAgentsList: searchedAgents,
    });
  };

  onAgentStatusViewChange = (selectedAgentStatus) => {
    this.setState({
      selectedAgentStatus,
    });
  };

  getTabsForAgentSelector = () => {
    const { selectedAgentStatus } = this.state;
    return [
      {
        value: AgentStatusView.READY,
        content: this.renderListOfAgents(AgentStatusView.READY),
        label: "Ready",
        count: this.getAgentTableRecords(AgentStatusView.READY).length,
        color: colors.supervisorStatGreen,
        active: AgentStatusView.READY === selectedAgentStatus,
      },
      {
        value: AgentStatusView.NOT_READY,
        content: this.renderListOfAgents(AgentStatusView.NOT_READY),
        label: "Not Ready",
        count: this.getAgentTableRecords(AgentStatusView.NOT_READY).length,
        color: colors.orange,
        active: AgentStatusView.NOT_READY === selectedAgentStatus,
      },
    ];
  };

  getAgentTableColumns = () => {
    const tableColumns = [
      {
        title: "Name",
        field: "name",
      },
      {
        title: "Active Session**",
        field: "activeSessionsCount",
      },
      {
        title: "Skill",
        field: "group",
      },
    ];

    const tableColumnsWithStyle = tableColumns.map((tc) => ({
      ...tc,
      cellStyle: {
        border: 0,
        fontWeight: "normal",
      },
      headerStyle: {
        border: 0,
        color: colors.darkGrey,
        fontWeight: "300",
        fontSize: "1em",
      },
    }));
    return tableColumnsWithStyle;
  };

  isSearching = () => {
    const { searchText } = this.state;
    return searchText.trim().length > 0;
  };

  getAgentTableRecords = (agentStatus) => {
    const { agentsList, searchedAgentsList } = this.state;
    let agentRecords = [];
    if (agentStatus === AgentStatusView.READY) {
      agentRecords = agentsList.filter(
        (agent) => agent.status === UserStatus.READY
      );
    } else if (agentStatus === AgentStatusView.NOT_READY) {
      agentRecords = agentsList.filter(
        (agent) => agent.status !== UserStatus.READY
      );
    }
    if (this.isSearching()) {
      agentRecords = agentRecords.filter((agent) =>
        searchedAgentsList.some(
          (searchedAgent) => searchedAgent.agentId === agent.agentId
        )
      );
    }
    return agentRecords;
  };

  renderListOfAgents = (agentStatus) => {
    const { selectedAgent, isLoading } = this.state;
    const onRowClick = (event, row) => {
      this.setState({
        selectedAgent: row.agentId,
        selectedAgentGroup: row.group,
      });
    };
    return (
      <MaterialTable
        localization={{
          body: {
            emptyDataSourceMessage: this.isSearching() ? (
              <p>No agents matching the search</p>
            ) : (
              <p>No other agents logged in.</p>
            ),
          },
        }}
        icons={tableIcons}
        columns={this.getAgentTableColumns()}
        data={this.getAgentTableRecords(agentStatus)}
        options={{
          search: false,
          paging: false,
          showTitle: false,
          toolbar: false,
          draggable: false,
          rowStyle: (row) => {
            const isSelected = row.agentId === selectedAgent;
            return {
              color: isSelected ? colors.lightPeach : colors.darkGrey,
              backgroundColor: isSelected ? colors.subtleYellow : "#fff",
            };
          },
        }}
        style={{ boxShadow: "none" }}
        onRowClick={
          agentStatus === AgentStatusView.READY ? onRowClick : undefined
        }
        isLoading={isLoading}
      />
    );
  };

  isConfirmDisabled = () => {
    return this.state.selectedAgent === null || this.state.isTransferring;
  };

  onTransferCancel = () => {
    this.props.changeAgentView(AgentViewTypes.DEFAULT);
  };

  getActiveConversation = () => {
    const { activeConversationId, conversations } = this.props;
    return conversations.find((c) => c.conversationId === activeConversationId);
  };

  onConfirmTransfer = () => {
    this.setState({
      isTransferring: true,
    });
    const { selectedAgent, selectedAgentGroup } = this.state;
    const fromAgentId = getUsername();
    const conversationToTransfer = this.getActiveConversation();
    const apiPayloadToTransfer = {
      fromAgentId,
      toAgentId: selectedAgent,
      conversationId: conversationToTransfer.conversationId,
      phoneNumber: conversationToTransfer.conversationMeta.phoneNumber,
      customerId: conversationToTransfer.conversationMeta.customerId,
      toGroup: selectedAgentGroup,
      fromGroup: this.group,
      channel: conversationToTransfer.conversationMeta.channel,
    };
    uepClient()
      .post(`/conversation/transfer-chat`, apiPayloadToTransfer)
      .then(() => {
        console.info(
          `Conversation ${conversationToTransfer.conversationId} is transferred from ${fromAgentId} to ${selectedAgent}`
        );
        const agent = this.state.agentsList.find(
          (a) => a.agentId === selectedAgent
        );
        this.props.changeAgentView(AgentViewTypes.DEFAULT);
        this.props.transferConversation({
          ...conversationToTransfer,
          agentId: selectedAgent,
          agentName: agent.name,
          status: ConversationStatus.TRANSFERRED,
        });
      })
      .catch((error) => {
        if (error && error.response && error.response.status === 409) {
          const errorMessage = error.response.data;
          this.props.showAlert({
            variant: "danger",
            content: (
              <span>
                <b>Error! </b> {errorMessage}
              </span>
            ),
          });
        } else {
          this.props.showAlert({
            variant: "danger",
            content: (
              <span>
                <b>Error! </b> Something went wrong while transferring!
              </span>
            ),
          });
        }
      })
      .finally(() => {
        this.setState({
          isTransferring: false,
        });
      });
  };

  render() {
    const { className } = this.props;
    const { searchText, isTransferring } = this.state;
    return (
      <div className={`agent-transfer-selector ${className}`}>
        <div className="agent-search-container">
          <Input
            name="agent-search-input"
            className="agent-search-input"
            type="text"
            onChange={this.onSearchTextChange}
            value={searchText}
            placeholder="Search Agent Name/skills/group"
          />
        </div>
        <div className="agents-listing">
          <div className="agents-status-tabs">
            <CollapsibleTabs
              className="agent-selector-collapsible-tabs"
              tabs={this.getTabsForAgentSelector()}
              onChange={this.onAgentStatusViewChange}
            />
          </div>
          <div className="buttons-container">
            <Button
              className="cancel-btn"
              isTransparent
              label="Cancel"
              onClick={this.onTransferCancel}
            />
            <Button
              className="confirm-btn"
              isYellowButton
              label="Confirm"
              disabled={this.isConfirmDisabled()}
              onClick={this.onConfirmTransfer}
              isLoading={isTransferring}
            />
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  activeConversationId: state.conversation.activeConversationId,
  conversations: state.conversation.conversations,
});

const mapDispatchToProps = {
  changeAgentView: changeAgentView,
  transferConversation: transferConversation,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(WithAlert(AgentTransferSelector));
